import { useCallback, useEffect, useState } from 'react'
import { StyleSheet } from 'react-native'
import { Button, Dialog, Portal, Text, TextInput, useTheme } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useAvoidSoftInputForModal } from '@/hooks/useAvoidSoftInputForModal'
import { useDismissOnEscape } from '@/hooks/useDismissOnEscape'

interface RejectDialogProps {
    visible: boolean
    onDismiss: () => void
    onConfirm: (reason: string) => void
    isLoading: boolean
}

export function RejectDialog({ visible, onDismiss, onConfirm, isLoading }: RejectDialogProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()
    const [reason, setReason] = useState('')

    useAvoidSoftInputForModal(visible)
    useDismissOnEscape(visible, onDismiss)

    useEffect(() => {
        if (visible) {
            setReason('')
        }
    }, [visible])

    const handleConfirm = useCallback(() => {
        onConfirm(reason)
    }, [onConfirm, reason])

    return (
        <Portal>
            <Dialog visible={visible} onDismiss={onDismiss} style={styles.dialog}>
                <Dialog.Title>Reject Time Entry</Dialog.Title>
                <Dialog.Content>
                    <Text
                        variant="bodyMedium"
                        style={[styles.text, { color: theme.colors.onSurfaceVariant }]}
                    >
                        Optionally provide a reason for rejecting this time entry:
                    </Text>
                    <TextInput
                        autoFocus
                        value={reason}
                        onChangeText={setReason}
                        mode="outlined"
                        multiline
                        numberOfLines={4}
                        placeholder="Enter rejection reason..."
                        style={styles.input}
                    />
                </Dialog.Content>
                <Dialog.Actions>
                    <Button onPress={onDismiss}>Cancel</Button>
                    <Button
                        onPress={handleConfirm}
                        loading={isLoading}
                        disabled={isLoading}
                        textColor={semanticColors.error}
                    >
                        Reject
                    </Button>
                </Dialog.Actions>
            </Dialog>
        </Portal>
    )
}

const styles = StyleSheet.create({
    dialog: {
        top: 100,
        marginBottom: 300,
        maxWidth: 500,
        alignSelf: 'center',
        width: '100%',
    },
    text: {
        marginBottom: 16,
    },
    input: {
        marginTop: 8,
    },
})
